// ─────────────────────────────────────────────────────────────────
// Glass-i — Expense Entry API client (Stage 6B.7).
// ─────────────────────────────────────────────────────────────────

import client from '../../../../api/client';
import { paymentVoucherApi } from '../payments/paymentVoucherApi';

const BASE = '/api/erp/accounts';
const EXP  = `${BASE}/expenses`;
const CAT  = `${BASE}/expense-categories`;

function unwrap(resp) {
  return resp && resp.data && resp.data.data !== undefined
    ? resp.data.data
    : resp && resp.data;
}

const GRID_FIELD_TO_SQL = {
  expenseNo:     'expense_no',
  expenseDate:   'expense_date',
  expenseType:   'expense_type',
  totalAmount:   'total_amount',
  status:        'status',
  paymentStatus: 'payment_status',
  postedAt:      'posted_at',
  createdAt:     'created_at'
};
function mapField(c) { return c && typeof c === 'string' ? (GRID_FIELD_TO_SQL[c] || c) : c; }
function mapRules(rules) {
  return rules.map((r) => {
    if (!r || typeof r !== 'object') return r;
    if (Array.isArray(r.rules)) return { ...r, rules: mapRules(r.rules) };
    if (typeof r.field === 'string' && r.field) return { ...r, field: mapField(r.field) };
    return r;
  });
}
function translateQuery(q) {
  if (!q || typeof q !== 'object') return q;
  const out = { ...q };
  if (Array.isArray(out.filters)) {
    out.filters = out.filters.filter((f) => f && f.field).map((f) => ({ ...f, field: mapField(f.field) }));
  }
  if (Array.isArray(out.sort)) {
    out.sort = out.sort.filter((s) => s && s.field).map((s) => ({ ...s, field: mapField(s.field) }));
  }
  if (typeof out.groupBy === 'string' && out.groupBy) out.groupBy = mapField(out.groupBy);
  if (out.advancedFilters && Array.isArray(out.advancedFilters.rules)) {
    out.advancedFilters = { ...out.advancedFilters, rules: mapRules(out.advancedFilters.rules) };
  }
  return out;
}

export const expenseEntryApi = {
  list: (query) =>
    client.post(`${EXP}/list`, translateQuery(query) || {}).then(unwrap),
  get:  (id) => client.get(`${EXP}/${encodeURIComponent(id)}`).then(unwrap),
  createDraft: (payload) => client.post(EXP, payload).then(unwrap),
  updateDraft: (id, payload) =>
    client.put(`${EXP}/${encodeURIComponent(id)}`, payload).then(unwrap),
  post: (id, { approvalMfaChallengeId } = {}) =>
    client.post(`${EXP}/${encodeURIComponent(id)}/post`, {}, {
      headers: approvalMfaChallengeId
        ? { 'x-approval-mfa-challenge-id': approvalMfaChallengeId }
        : undefined
    }).then(unwrap),
  cancel: (id, body, { approvalMfaChallengeId } = {}) =>
    client.post(`${EXP}/${encodeURIComponent(id)}/cancel`, body || {}, {
      headers: approvalMfaChallengeId
        ? { 'x-approval-mfa-challenge-id': approvalMfaChallengeId }
        : undefined
    }).then(unwrap),
  // Stage 6B.9F — draft delete now carries the Approval-MFA challenge.
  remove: (id, { approvalMfaChallengeId } = {}) =>
    client.delete(`${EXP}/${encodeURIComponent(id)}`, {
      headers: approvalMfaChallengeId
        ? { 'x-approval-mfa-challenge-id': approvalMfaChallengeId }
        : undefined
    }).then(unwrap),

  // Tax-calc preview (read-only).
  calculateTax: (payload) => client.post(`${EXP}/calculate-tax`, payload).then(unwrap),

  // Categories
  listCategories: (q = '') => client.get(CAT, { params: { q } }).then(unwrap),
  createCategory: (payload) => client.post(CAT, payload).then(unwrap),
  updateCategory: (id, payload) =>
    client.put(`${CAT}/${encodeURIComponent(id)}`, payload).then(unwrap),

  // Re-export the lookups already wired by the Payment Voucher module.
  supplierLookup:       paymentVoucherApi.supplierLookup,
  cashBankLedgerLookup: paymentVoucherApi.cashBankLedgerLookup
};

export default expenseEntryApi;
